import { useMemo } from 'react';
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import keyBy from 'lodash/keyBy';
import isEqual from 'lodash/isEqual';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
//
import { deepMapTree } from 'utils/tree';
import { FETCH_CONTENT_TAG_TREE } from 'meeopp-graphql/modules/content-tags';
import { CONTENT_TAG_STATUS_MAP, CONTENT_TAG_CATEGORIES_MAP } from 'utils/constants';
import { CustomTreeLogic } from './custom-tree-logic';

export const DEFAULT_TREE_ROOT_CATEGORIES = Object.freeze([
  CONTENT_TAG_CATEGORIES_MAP.curriculum,
  CONTENT_TAG_CATEGORIES_MAP.curriculumLevel,
  CONTENT_TAG_CATEGORIES_MAP.curriculumLevelTwo,
  CONTENT_TAG_CATEGORIES_MAP.theme,
  CONTENT_TAG_CATEGORIES_MAP.subtheme,
  CONTENT_TAG_CATEGORIES_MAP.focus,
  CONTENT_TAG_CATEGORIES_MAP.topic,
  CONTENT_TAG_CATEGORIES_MAP.misc,
  CONTENT_TAG_CATEGORIES_MAP.difficulty,
  CONTENT_TAG_CATEGORIES_MAP.type,
  CONTENT_TAG_CATEGORIES_MAP.meeoppCycleExerciseType,
  CONTENT_TAG_CATEGORIES_MAP.learningFocus,
]);

// Custom memoize logic that performs deep comparison on the selector arguments
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const contentTagsSelector = (state) => state.contentTags.contentTags;
const contentTagsTreeSelector = createDeepEqualSelector(
  (state) => state.contentTags.contentTagsTree,
  (state, treeRootCategories) => treeRootCategories,
  (contentTagsTree, treeRootCategories) =>
    contentTagsTree.filter((tag) => treeRootCategories.includes(tag.category))
);

const globalCurriculumTreeSelector = (state) => state.contentTags.globalCurriculumTree;

const contentTagsByTitleSelector = createSelector(contentTagsSelector, (tags) =>
  keyBy(tags, ({ displayTitle, title }) => displayTitle ?? title)
);

const contentTagsByCodeSelector = createSelector(contentTagsSelector, (tags) =>
  keyBy(tags, ({ code }) => code)
);

const globalMeeoppTopicTreeSelector = (state) => state.contentTags.globalMeeoppTopicTree;

const contentTagsByIdSelector = createSelector(contentTagsSelector, (tags) => keyBy(tags, '_id'));

const activeContentTagsSelector = createSelector(contentTagsSelector, (tags) =>
  tags.filter((tag) => tag.status === CONTENT_TAG_STATUS_MAP.published)
);

const activeContentTagsTreeSelector = createDeepEqualSelector(contentTagsTreeSelector, (tree) =>
  tree.filter((tag) => tag.status === CONTENT_TAG_STATUS_MAP.published)
);

const curriculumContentTagsSelector = createDeepEqualSelector(contentTagsTreeSelector, (tree) =>
  tree.filter((tag) => tag.category === CONTENT_TAG_CATEGORIES_MAP.curriculum)
);

/**
 * This aims to find all thtmes/subthemes base on the class' settings
 * Logic is pretty complicated, brief explanation:
 * If you tree looks like this:
 *      IB DP--------
 *     /     \        \
 *    /       \        \
 * Theme A  IB HL B   IB SL B
 *            / \
 *           /   \
 *     Theme B   Theme C
 *
 * For an IB HL B Class, they should be able to see all Theme A, B and C.
 * For an IB SL B Class, they should only be able to see Theme A, as Theme B and C belongs to IB HL B
 * If there are no displayable themes for a curriculum/curriculum level, display all visible tags to the org.
 */
const findThemeTagsByCurriculumSelector = createDeepEqualSelector(
  contentTagsTreeSelector,
  contentTagsByIdSelector,
  contentTagsSelector,
  (contentTagsTree, contentTagsMapById) => (tagIds) => {
    const isThemesOrSubthemes = (tag) =>
      [CONTENT_TAG_CATEGORIES_MAP.theme, CONTENT_TAG_CATEGORIES_MAP.subtheme].includes(
        tag.category
      );

    if (!tagIds) {
      return [];
    }

    const findPathToTag = (tag) => {
      if (!tag.parentId) return [tag._id];
      return [...findPathToTag(contentTagsMapById[tag.parentId]), tag._id];
    };

    const flattenTagTree = (node) => {
      const { children, ...restOfTheNode } = node;
      return [
        restOfTheNode,
        ...(children || []).reduce((pv, tag) => [...pv, ...flattenTagTree(tag)], []),
      ];
    };

    const getThemesSubthemesByPath = (path, currentTree) => {
      if (!currentTree) return [];

      const currentLevelThemesAndSubthemes = currentTree
        .filter(isThemesOrSubthemes)
        .reduce((pv, tag) => [...pv, ...flattenTagTree(tag)], [])
        .filter(isThemesOrSubthemes);

      if (!path.length) return currentLevelThemesAndSubthemes;

      const [nextTagId, ...restOfThePath] = path;
      const foundTag = currentTree.find((tag) => tag._id === nextTagId);

      if (!foundTag) return [];

      return [
        ...currentLevelThemesAndSubthemes,
        ...getThemesSubthemesByPath(restOfThePath, foundTag.children),
      ];
    };

    /**
     * Logic:
     * 1. Find the path to the designated tags (i.e. Given HB HL B, we retrieve the path IB DP -> IB HL B)
     * 2. Get all themes and subthemes for every node within the path
     *    (i.e. getThemesAndSubthemes(IB DP) + getThemesAndSubthemes(IB HL B))
     * 3. If there are no themes/subthemes for the curriculum, return all visible tags
     */
    let themesAndSubthemesFromPath = [];
    for (let tagId of tagIds) {
      const tag = contentTagsMapById[tagId];
      if (!tag) continue;

      const pathToTag = findPathToTag(tag);
      const themesAndSubthemesFromTagPath = getThemesSubthemesByPath(pathToTag, contentTagsTree);
      themesAndSubthemesFromPath = [
        ...themesAndSubthemesFromPath,
        ...themesAndSubthemesFromTagPath,
      ];
    }

    return themesAndSubthemesFromPath;
  }
);

const miscTagsSelector = createSelector(contentTagsSelector, (tags) =>
  tags.filter((tag) => tag.category === CONTENT_TAG_CATEGORIES_MAP.misc)
);

const globalTagsSelector = createSelector(contentTagsSelector, (tags) =>
  tags.filter((tag) => !!tag.isGloballyVisible)
);

const customTreeLogicSelector = createDeepEqualSelector(
  contentTagsSelector,
  contentTagsTreeSelector,
  contentTagsByIdSelector,
  contentTagsByTitleSelector,
  (contentTags, contentTagsTree, contentTagsMapById, contentTagsMapByTitle) =>
    new CustomTreeLogic({
      contentTagSelectors: {
        contentTags,
        contentTagsTree,
        contentTagsMapById,
        contentTagsMapByTitle,
      },
    })
);

// build a tree that contains only subthemes as leaf nodes
const subthemeTagTreeSelector = createDeepEqualSelector(
  contentTagsTreeSelector,
  (contentTagsTree) => {
    // recursively check whether the node is a subtheme or one of its descendants contain subtheme
    const hasSubtheme = (node) => {
      if (node.category === CONTENT_TAG_CATEGORIES_MAP.subtheme) {
        return true;
      }

      return (node.children || []).some(hasSubtheme);
    };

    // recursively filter out tree children that is neither a subtheme nor having a descendant with subtheme
    const filterTreeChildren = (node) => {
      const filteredChildren = (node.children || []).filter(hasSubtheme).map(filterTreeChildren);

      return {
        ...node,
        children: filteredChildren,
      };
    };

    return contentTagsTree.filter(hasSubtheme).map(filterTreeChildren);
  }
);

const contentTagFullPathSelector = createSelector(
  contentTagsByIdSelector,
  (contentTagsMapById) => (contentTagId) => {
    const fullPath = [];

    for (let tag = contentTagsMapById[contentTagId]; tag; tag = contentTagsMapById[tag.parentId]) {
      fullPath.unshift(tag);
    }

    return fullPath;
  }
);

const globalCurriculumTagsByIdSelector = createSelector(
  (state) => state.contentTags.globalCurriculumTree,
  (globalCurriculumTree) => {
    let queue = [...globalCurriculumTree];
    let tagsById = {};

    while (queue.length > 0) {
      let current = queue.shift();
      tagsById[current._id] = current;
      queue.push(...current.children);
    }

    return tagsById;
  }
);

const makeSelector = () =>
  createDeepEqualSelector(
    (state, treeRootCategories, overrideState) => ({ ...state, ...overrideState }),
    (state, treeRootCategories) => treeRootCategories,
    (state, treeRootCategories) => {
      const { contentTags } = state;
      return {
        contentTags: contentTags.contentTags,
        contentTagsMapById: contentTagsByIdSelector(state),
        contentTagsMapByTitle: contentTagsByTitleSelector(state),
        contentTagsMapByCode: contentTagsByCodeSelector(state),
        activeContentTags: activeContentTagsSelector(state),
        contentTagsTree: contentTagsTreeSelector(state, treeRootCategories),
        globalCurriculumTree: globalCurriculumTreeSelector(state),
        globalCurriculumTagsById: globalCurriculumTagsByIdSelector(state),
        globalMeeoppTopicTree: globalMeeoppTopicTreeSelector(state),
        activeContentTagsTree: activeContentTagsTreeSelector(state, treeRootCategories),
        curriculumContentTagTrees: curriculumContentTagsSelector(state, treeRootCategories),
        miscTags: miscTagsSelector(state),
        globalTags: globalTagsSelector(state),
        customTreeLogic: customTreeLogicSelector(state, treeRootCategories),
        findThemeTagsByCurriculum: findThemeTagsByCurriculumSelector(state, treeRootCategories),
        subthemeTagTree: subthemeTagTreeSelector(state, treeRootCategories),
        getContentTagFullPath: contentTagFullPathSelector(state),
      };
    }
  );

const useContentTags = (treeRootCategories = DEFAULT_TREE_ROOT_CATEGORIES) => {
  const selector = useMemo(makeSelector, [treeRootCategories]);
  return useSelector((state) => selector(state, treeRootCategories));
};

const useOrgContentTags = (organizationId, treeRootCategories = DEFAULT_TREE_ROOT_CATEGORIES) => {
  const { data } = useQuery(FETCH_CONTENT_TAG_TREE, {
    variables: {
      viewAsOrgId: organizationId,
      displayOptions: { organizationId },
    },
    skip: !organizationId,
  });

  const overrideState = {
    contentTags: {
      contentTags: data?.contentTagTree?.allNodes ?? [],
      contentTagsTree: deepMapTree(data?.contentTagTree?.topLevelNodes ?? []),
      globalCurriculumTree: deepMapTree(data?.globalNodes?.curriculums ?? []),
      globalMeeoppTopicTree: deepMapTree(data?.globalNodes?.meeoppTopics ?? []),
    },
  };

  const selector = useMemo(makeSelector, [treeRootCategories, organizationId]);
  return useSelector((state) =>
    selector(state, treeRootCategories, organizationId ? overrideState : {})
  );
};

export { useContentTags, useOrgContentTags };
